import {Children, cloneElement, EffectCallback, FC, isValidElement, useCallback, useEffect, useRef} from 'react';

import {isLatLngLiteral} from '@googlemaps/typescript-guards';
import {createCustomEqual} from 'fast-equals';

import {MapProps} from './types';

export const Map: FC<MapProps> = ({
  onClick,
  setMap,
  map,
  onIdle,
  children,
  style,
  onCenterChanged,
  onDragEnd,
  onZoomChanged,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && !map) {
      setMap && setMap(new window.google.maps.Map(ref.current, {clickableIcons: false}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, map]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, options]);

  const stableOnIdle = useCallback(() => {
    if (map && onIdle) onIdle(map);
  }, [map, onIdle]);

  useEffect(() => {
    if (map) {
      ['click', 'idle', 'center_changed', 'dragend', 'zoom_changed'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (stableOnIdle) {
        map.addListener('idle', stableOnIdle);
      }

      if (onCenterChanged) {
        map.addListener('center_changed', () => onCenterChanged(map));
      }

      if (onDragEnd) {
        map.addListener('dragend', () => onDragEnd(map));
      }
      if (onZoomChanged) {
        map.addListener('zoom_changed', () => onZoomChanged(map));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, onClick, stableOnIdle, onCenterChanged]);

  return (
    <>
      <div ref={ref} style={style} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return cloneElement(child, {map});
        }
      })}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a: any, b: any) => {
  if (isLatLngLiteral(a) || a instanceof google.maps.LatLng || isLatLngLiteral(b) || b instanceof google.maps.LatLng) {
    return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
  }

  // TODO extend to other types

  // use fast-equals for other objects
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return deepEqual(a, b);
});

function useDeepCompareMemoize(value: any) {
  const ref = useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(callback: EffectCallback, dependencies: any[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
